import * as CL from '@design-system/component-library';
import { useContext, useRef } from 'react';
import { useLocation, useNavigate, useRevalidator, useRouteLoaderData } from 'react-router';
import { paths } from '../../common/constants/pathVariables.js';
import {
  chosenLanguageMsg,
  closeMsg,
  englishMsg,
  logInMsg,
  logOutMsg,
  myInformationMsg,
  searchMsg,
  suomiMsg,
  svenskaMsg,
  t,
} from '../../common/i18n/index.js';
import { userHasApproverRole } from '../../common/utils/employeeUtils.js';
import { isStrictFeatureEnabled } from '../../common/utils/featureFlagUtils';
import { AuthenticatedUserRole, IdentityProvider } from '../../generated/api/models.js';
import { useAuth } from '../../public/site/AuthProvider.js';
import { useConfig } from '../../public/site/ConfigProvider.js';
import { SiteContext } from '../../public/site/SiteContext.js';
import { isElisaIdV3Enabled } from '../../public/site/authUtils.js';
import { CompanySelector } from '../CompanySelector/CompanySelector.js';
import { eoeUserMenuLinks } from '../EoeHeader/dynamic/eoeUserMenuLinks.js';
import { HeaderNotification } from '../HeaderNotification/HeaderNotification.js';
import { HeaderShoppingBasket } from '../HeaderShoppingCart/HeaderShoppingBasket.js';
import { HeaderShoppingBasketCoCo } from '../HeaderShoppingCart/HeaderShoppingBasketCoCo';
import { employeeHeaderAccordions } from './dynamic/employeeHeaderAccordions.js';
import { headerAccordions, loginLinks } from './dynamic/headerAccordions.js';
import { LoggedInAsEmployeeNotification, getUserAccounts, siteSelect } from './dynamic/headerFunctions.js';
import { personalUserMenuLinks } from './dynamic/personalUserMenuLinks.js';
import {
  mapNavigationGroupToCLHeaderMegaMenuSubMenu,
  publicNavigationLinks,
  publicNavigationLinksWithCurrentProperty,
} from './dynamic/publicNavigationLinks.js';
import type { HeaderUserAccount } from '../../common/types/authentication';
import type { PageResponse, SiteStructureNode } from '../../generated/api/models.js';
import type { NavigationResponse } from '../../generated/api/navigationResponse';
import type { BreadCrumbNameAndPath } from '../BreadCrumbs/index.js';
import type { ChangeEvent, KeyboardEvent, MouseEvent } from 'react';

import './Header.scss';

type HeaderProps = {
  // CMS pages redirect to language-specific landing page on language change,
  // OmaElisa and Employee portal do not.
  // Public site has swedish, OmaElisa and Employee portal do not.
  cmsPage?: boolean;
  isFullVersion?: boolean;
};

const ABSOLUTE_URL_RE = /^([a-z]+:\/\/|\/\/)/i;

export const getBreadCrumbLinks = (
  breadcrumbIds: number[],
  structure: SiteStructureNode[]
): { name: string; path: string }[] => {
  const result: BreadCrumbNameAndPath[] = [];
  breadcrumbIds.forEach(id => {
    return structure
      .filter(elem => id === elem.id)
      .forEach(({ path, name }) => {
        result.push({ path, name });
      });
  });
  return result;
};

const getPersonalLinks = ({
  isEmployeeUser,
  navigate,
  hasApproverRole,
  isElisaIdUser,
  prepareElisaIdLogin,
  classicSiteUrl,
  hasPendingMfa,
  companyName,
}: {
  isEmployeeUser: boolean;
  navigate: ReturnType<typeof useNavigate>;
  hasApproverRole: boolean;
  isElisaIdUser: boolean;
  prepareElisaIdLogin?: () => void;
  classicSiteUrl: string;
  hasPendingMfa: boolean;
  companyName: string;
}) => {
  if (isEmployeeUser) {
    return eoeUserMenuLinks(navigate, hasApproverRole, true, isElisaIdUser ? prepareElisaIdLogin : undefined);
  }

  const links = personalUserMenuLinks(navigate, classicSiteUrl, isElisaIdUser ? prepareElisaIdLogin : undefined);
  return hasPendingMfa && !companyName ? links?.filter(link => link.url === paths.SELF_SERVICE_HOME) : links;
};

export const Header = ({ cmsPage, isFullVersion = true }: HeaderProps) => {
  const { logout, ssoSessionValid, setActiveAccount, authenticatedUser, anonymousUser, prepareElisaIdLogin } =
    useAuth();
  const navigate = useNavigate();
  const { siteBaseUrl, siteLanguage, saveUserPreferredLanguage, switchPreferredLanguage } = useContext(SiteContext);
  const { pathname } = useLocation();
  const page = useRouteLoaderData('public-path-root') as PageResponse | undefined;
  const hamburgerRef = useRef<HTMLAnchorElement | HTMLButtonElement>(null);
  const menuId = 'public-navigation';

  const { classicSiteUrl, ringBaseUrl, featureFlags } = useConfig();

  const cocoEnabled = isStrictFeatureEnabled(featureFlags.coco);
  const isEmployeeUser = authenticatedUser?.userRole === AuthenticatedUserRole.EMPLOYEE;
  const { firstName = '', lastName = '', email = '', companyName = '' } = authenticatedUser || {};
  const isLoggedIn = Boolean(companyName);
  const fullName = `${firstName} ${lastName}`;
  const hasPendingMfa = anonymousUser?.mfaInfo !== undefined;
  const userAccounts = getUserAccounts(authenticatedUser);

  const selectedLanguage = siteLanguage.substring(0, 2);
  const homePath = selectedLanguage === 'fi' ? '/' : `/${selectedLanguage}`;

  const navigateTo = (path: string) => (e: MouseEvent) => {
    e.preventDefault();
    navigate(path);
  };

  const publicNavigationData = useRouteLoaderData('navigation-path') as NavigationResponse | undefined;
  const selfserviceNavigationData = useRouteLoaderData('selfservice-navigation-path') as NavigationResponse | undefined;
  const eoeNavigationData = useRouteLoaderData('eoe-navigation-path') as NavigationResponse | undefined;
  const rootNavigationData = useRouteLoaderData('site-navigation') as NavigationResponse | undefined;
  const navigationResponseFromRoute =
    publicNavigationData ?? selfserviceNavigationData ?? eoeNavigationData ?? rootNavigationData;
  const navigationData = navigationResponseFromRoute?.menu ?? [];

  const linkStructure = navigationData?.length
    ? mapNavigationGroupToCLHeaderMegaMenuSubMenu(navigationData)
    : publicNavigationLinks(siteBaseUrl);

  const { revalidate } = useRevalidator();

  const languageSelect = {
    languages: cmsPage
      ? [
          { name: suomiMsg, abbr: 'fi' },
          { name: svenskaMsg, abbr: 'sv' },
          { name: englishMsg, abbr: 'en' },
        ]
      : [
          { name: suomiMsg, abbr: 'fi' },
          { name: englishMsg, abbr: 'en' },
        ],
    selectedLanguage,
    onLanguageChange: ({ lang }: { lang: string }) => {
      (cmsPage ? switchPreferredLanguage : saveUserPreferredLanguage)(lang);
      revalidate().then();
    },
  };

  const navigationItemList = linkStructure
    ? publicNavigationLinksWithCurrentProperty(
        linkStructure,
        getBreadCrumbLinks(page?.breadcrumbs || [], page?.siteStructureNodes || []),
        pathname
      )
    : [];

  const accordions = isEmployeeUser ? employeeHeaderAccordions : loginLinks(navigate, ssoSessionValid);
  const hasApproverRole = userHasApproverRole(authenticatedUser);
  const isElisaIdUser = isElisaIdV3Enabled() && authenticatedUser?.identityProvider === IdentityProvider.ELISA_ID;

  const personalLinks = getPersonalLinks({
    isEmployeeUser,
    navigate,
    hasApproverRole,
    isElisaIdUser,
    prepareElisaIdLogin,
    classicSiteUrl,
    hasPendingMfa,
    companyName,
  });

  const getUser = () => {
    if (authenticatedUser) {
      return { email, fullName, name: isEmployeeUser ? fullName : firstName };
    }
    if (hasPendingMfa) {
      return { fullName: '', email: '', name: '' };
    }
    return undefined;
  };
  const user = getUser();

  return (
    <>
      <div className="of-public-header-wrapper">
        <CL.Header
          className={`of-public-header ${isElisaIdUser ? 'of-public-header--elisa-id' : ''}`}
          desktopBreakpointOffset={35} // update the breakpoint for of-public-header-wrapper as well if you change this
          isLoggedIn={isLoggedIn || hasPendingMfa}
          languageSelect={isFullVersion ? languageSelect : undefined}
          siteSelect={siteSelect(siteBaseUrl)}
        >
          <CL.HeaderNavigationSection>
            <CL.HeaderLogoAndService logo={{ label: 'Elisa', onClick: navigateTo(homePath), url: homePath }} />
            {isFullVersion && (
              <CL.HeaderNavigation
                id={menuId}
                languageSelect={languageSelect}
                navigationItemList={navigationItemList}
                onButtonClick={() => {}}
                onLinkClick={(e, { url }) => {
                  if (url && !ABSOLUTE_URL_RE.test(url)) {
                    e.preventDefault();
                    navigate(url);
                  }
                }}
                requireLogin={false}
                returnFocusToRef={hamburgerRef}
                siteSelect={siteSelect(siteBaseUrl)}
              />
            )}
          </CL.HeaderNavigationSection>
          <CL.HeaderNavigationSection>
            {isFullVersion && (
              <>
                <CL.HeaderSearchButton
                  href={paths.SEARCH_PUBLIC_PAGES}
                  i18nLabel={t.V4GK(searchMsg)}
                  onClick={navigateTo(paths.SEARCH_PUBLIC_PAGES)}
                />
                {cocoEnabled ? (
                  <HeaderShoppingBasketCoCo toShoppingCart={paths.SHOPPING_BASKET} />
                ) : (
                  <HeaderShoppingBasket toShoppingCart={paths.SHOPPING_BASKET} />
                )}
              </>
            )}
            <CL.HeaderServicesMenu
              i18nButtonLabel={t.A77Z('Navigate')}
              servicesMenuLinkAccordions={headerAccordions(navigate, ringBaseUrl)}
            />
            <CL.HeaderUserMenu
              i18nButtonLabel={isLoggedIn ? t.MIFJ(myInformationMsg) : t.Z16I(logInMsg)}
              i18nLanguageSwitchSelectedLanguageLabel={t.FT4V(chosenLanguageMsg)}
              i18nLogin={t.Z16I(logInMsg)}
              logout={{ onClick: () => logout(paths.SELF_SERVICE_LOGOUT), title: t.LQ3X(logOutMsg) }}
              notifications={personalLinks}
              personalLinks={
                authenticatedUser && userAccounts.length > 0 && !isEmployeeUser
                  ? [
                      <CompanySelector
                        id="userMenuCompanySelector"
                        key="headerCompanySelector"
                        onInputChange={(
                          _e: ChangeEvent | KeyboardEvent | MouseEvent,
                          option: Partial<HeaderUserAccount>
                        ) => {
                          if (option?.accountMasterId) {
                            setActiveAccount(option.accountMasterId, true);
                          }
                        }}
                        userAccounts={userAccounts}
                      />,
                    ]
                  : []
              }
              user={user}
              userPronounText={firstName}
              userMenuLinkAccordions={accordions}
            />
            {isFullVersion && (
              <CL.HeaderHamburger
                i18nCloseAlt={t.WOYD(closeMsg)}
                i18nOpenAlt="menu"
                componentRef={hamburgerRef}
                toggledModalChild={menuId}
              />
            )}
          </CL.HeaderNavigationSection>
        </CL.Header>
      </div>
      <HeaderNotification />
      {isEmployeeUser && pathname !== paths.SHOPPING_BASKET && LoggedInAsEmployeeNotification}
    </>
  );
};
